import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { Save, RotateCcw, ImageIcon } from "lucide-react";

const FIELDS = [
  { group: "Brand", items: [
    { key: "brand_name", label: "Brand name", type: "text", help: "Shown in the header, footer, meta tags." },
    { key: "tagline", label: "Hero tagline", type: "text", help: 'Use "…" to split into two lines on the homepage (e.g. "Doctor chahiye… milega.").' },
    { key: "hero_subtitle", label: "Hero subtitle", type: "textarea", help: "The paragraph under the main headline on the homepage." },
    { key: "meta_description", label: "SEO meta description", type: "textarea", help: "160 characters max. Used by Google and social previews on the homepage." },
    { key: "logo_url", label: "Logo image URL", type: "url", help: "Square logo image URL. Leave empty to use the default stethoscope mark." },
    { key: "footer_tagline", label: "Footer tagline", type: "textarea", help: "Short sentence under the logo in the footer." },
  ]},
  { group: "About page", items: [
    { key: "about_mission", label: "Mission statement", type: "textarea", help: "The first paragraph on the About page." },
  ]},
  { group: "Founder", items: [
    { key: "founder_name", label: "Founder name", type: "text" },
    { key: "founder_title", label: "Founder title", type: "text" },
    { key: "founder_bio", label: "Founder quote / bio", type: "textarea" },
    { key: "founder_photo_url", label: "Founder photo URL", type: "url", help: "Square or portrait photo. Leave empty to show initials." },
    { key: "founder_email", label: "Founder email", type: "email" },
    { key: "founder_linkedin_url", label: "Founder LinkedIn URL", type: "url" },
  ]},
  { group: "Contact", items: [
    { key: "support_email", label: "Support email", type: "email" },
    { key: "doctors_email", label: "Clinics / doctors email", type: "email" },
    { key: "whatsapp_number", label: "Business WhatsApp number", type: "tel", help: "Include country code, digits only (e.g. 919876543210)." },
  ]},
];

export default function SiteSettingsTab() {
  const { settings: current, refresh } = useSettings();
  const [form, setForm] = useState(current);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(current);
  }, [current]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/site-settings", form);
      await refresh();
      toast.success("Site settings saved. Changes are live.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setForm(current);
    toast.info("Reverted unsaved changes.");
  };

  return (
    <form onSubmit={save} className="mt-6 grid gap-8" data-testid="site-settings-form">
      <div className="rounded-xl border border-border bg-card p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="h-16 w-16 rounded-xl ring-1 ring-border bg-muted overflow-hidden grid place-items-center">
          {form.logo_url ? (
            <img src={form.logo_url} alt="Logo preview" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon size={22} className="text-muted-foreground" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-display font-semibold">Logo preview</p>
          <p className="text-sm text-muted-foreground">Paste a direct image URL above. We'll show it in the header and footer within seconds.</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button type="button" onClick={reset} className="btn-secondary" data-testid="settings-reset">
            <RotateCcw size={15} /> Reset
          </button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="settings-save-top">
            <Save size={15} /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {FIELDS.map((group) => (
        <section key={group.group} className="rounded-xl border border-border p-6 bg-card">
          <h3 className="font-display font-semibold text-lg">{group.group}</h3>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {group.items.map((f) => (
              <label key={f.key} className={f.type === "textarea" ? "md:col-span-2 text-sm" : "text-sm"}>
                <span className="font-medium">{f.label}</span>
                {f.type === "textarea" ? (
                  <textarea
                    rows={3}
                    className="input-base mt-1"
                    value={form[f.key] || ""}
                    onChange={update(f.key)}
                    data-testid={`settings-${f.key}`}
                  />
                ) : (
                  <input
                    type={f.type}
                    className="input-base mt-1"
                    value={form[f.key] || ""}
                    onChange={update(f.key)}
                    data-testid={`settings-${f.key}`}
                  />
                )}
                {f.help && <span className="block text-xs text-muted-foreground mt-1">{f.help}</span>}
              </label>
            ))}
          </div>
        </section>
      ))}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={reset} className="btn-secondary">
          <RotateCcw size={15} /> Reset
        </button>
        <button type="submit" disabled={saving} className="btn-primary" data-testid="settings-save">
          <Save size={15} /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </form>
  );
}
